import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import type { HourLimitMode } from '@/pages/hr/useAttendanceBoard';

/**
 * 52시간 셈 (V543 · DOC-130 §5 · `WorkHourController`). 셈은 서버 한 곳(`WorkHourLimitService`)이 하고 화면은
 * 받은 분을 그대로 그린다 — PC 잠금이 보는 숫자와 화면의 숫자가 같아야 한다.
 */

export type HourStatus = {
  personnelId: string;
  userId?: string | null;
  name: string;
  placeType?: 'DEPARTMENT' | 'SITE' | null;
  place?: string | null;
  mode: HourLimitMode;
  periodFrom: string;
  periodTo: string;
  limitMinutes: number;
  countedMinutes: number;
  voluntaryMinutes: number;
  emergencyMinutes: number;
  locked: boolean;
  alert: boolean;
  working: boolean;
};

export const HOURS_BASE = '/v1/hr/attendance-hours';

export function useAttendanceHours(date: string) {
  return useQuery({
    queryKey: ['hr-attendance-hours', date],
    queryFn: () => apiFetch<HourStatus[]>(`${HOURS_BASE}?date=${date}`),
  });
}

/** 셈 기간의 하루(`WorkHourLimitService.detail`). 분 단위. endBasis 는 끝 시각의 근거. */
export type HourDay = {
  date: string;
  in: string;
  end: string;
  endBasis: 'OUT' | 'NOW' | 'PC' | 'POLICY';
  spanMinutes: number;
  restMinutes: number;
  voluntaryMinutes: number;
  emergencyMinutes: number;
  countedMinutes: number;
};

/** 근무시간 탭의 상세 모달 — 한 사람의 셈 기간 날짜별 표(2026-10-26). 재시작 전 서버면 실패한다. */
export function useAttendanceHourDetail(personnelId: string, date: string) {
  return useQuery({
    queryKey: ['hr-attendance-hours', date, personnelId],
    queryFn: () => apiFetch<{ status: HourStatus; days: HourDay[] } | null>(`${HOURS_BASE}/${personnelId}?date=${date}`),
    retry: false,
  });
}

/** 본인 것. 인사자료와 이어지지 않은 계정이면 `null`. 사용자 서버가 재시작 전이면 실패한다 — 그때는 줄을 안 그린다. */
export function useMyAttendanceHours() {
  return useQuery({
    queryKey: ['me-attendance-hours'],
    queryFn: () => apiFetch<HourStatus | null>('/v1/me/attendance-hours'),
    retry: false,
  });
}

/** 230.3 처럼 소수 한 자리, 끝의 0 은 뗀다. */
export function hourNumber(minutes: number): string {
  return String(Math.round((minutes / 60) * 10) / 10);
}

/** 셈 h / 한도 h. */
export function hourRatio(status: Pick<HourStatus, 'countedMinutes' | 'limitMinutes'>): string {
  return `${hourNumber(status.countedMinutes)} h / ${hourNumber(status.limitMinutes)} h`;
}

/** 한도의 90% 를 넘으면 주황, 잠기면 빨강(DOC-130 §5-5). */
export function hourTone(status: HourStatus): 'red' | 'orange' | null {
  if (status.locked) return 'red';
  if (status.limitMinutes > 0 && status.countedMinutes >= status.limitMinutes * 0.9) return 'orange';
  return null;
}
